import React, { useMemo, useContext, createContext, useEffect } from 'react'
import { useAnimation } from '../animations.jsx'
import { useGLTF, Merged, useTexture } from '@react-three/drei'
import * as THREE from 'three'

const context = createContext()
export function Instances({ children, selectiveBloomEnabled, ...props }) {
  const { nodes, materials } = useGLTF('/models/Black_Arrow.glb')
  // Ensure all node meshes cast and receive shadows
  React.useEffect(() => {
    if (!nodes) return
    Object.values(nodes).forEach((node) => {
      if (node && node.isMesh) {
        node.castShadow = true
        node.receiveShadow = true
        node.frustumCulled = false // Disable frustum culling
        node.renderOrder = 10 // Ensure rocket renders after ground
      }
    })
  }, [nodes])
  
  // Handle selective bloom layer assignment
  React.useEffect(() => {
    if (!nodes) return
    Object.values(nodes).forEach((node) => {
      if (node && node.isMesh) {
        if (selectiveBloomEnabled) {
          node.layers.enable(1) // Enable layer 1 for selective bloom
        } else {
          node.layers.disable(1) // Disable layer 1, keep default layer 0
        }
      }
    })
  }, [nodes, selectiveBloomEnabled])
  // Also set castShadow/receiveShadow on all instance meshes
  const aoMaps = useTexture({
    BA_Fairing: '/textures/black_arrow_ao/Black_Arrow_M_BA_Fairing_AO.png',
    BA_S1_Body: '/textures/black_arrow_ao/Black_Arrow_M_BA_S1_Body_AO.png',
    BA_S1_Thruster: '/textures/black_arrow_ao/Black_Arrow_M_BA_S1_Thruster_AO.png',
    BA_S2_Body: '/textures/black_arrow_ao/Black_Arrow_M_BA_S2_Body_AO.png',
    BA_S2_Engine: '/textures/black_arrow_ao/Black_Arrow_M_BA_S2_Engine_AO.png',
  })
  if (materials && aoMaps) {
      Object.entries(aoMaps).forEach(([aoKey, aoMap]) => {
        if (aoMap) aoMap.flipY = false
        Object.keys(materials).forEach((matKey) => {
          if (matKey.toLowerCase().includes(aoKey.toLowerCase().replace('ba_', ''))) {
            materials[matKey].aoMap = aoMap
            materials[matKey].needsUpdate = true
          }
        })
      })
  }
  
  const instances = useMemo(
    () => ({
      Left: nodes.Left,
      Tip: nodes.Tip,
      Right: nodes.Right,
      EngineBrace: nodes.Engine_Brace,
      STB: nodes.ST1B,
      STBody: nodes.ST2_Body,
      STBody1: nodes.ST1_Body,
      STEngine: nodes.ST1_Engine,
      Prospero: nodes.Prospero,
      ThirdStage: nodes.ThirdStage,
    }),
    [nodes]
    
  )
  return (
    <Merged meshes={instances} {...props} frustumCulled={false}>
      {(instances) => <context.Provider value={instances} children={children} />}
    </Merged>
  )
}

export function Model(props) {
  const instances = useContext(context)
  const { nodes } = useGLTF('/models/Black_Arrow.glb')
  const { Stage_1_Offset, Stage_2_Offset, Stage_3_Offset, leftFairingRotation, rightFairingRotation } = useAnimation()
  const [bounds, setBounds] = React.useState(null)
  
  // Control visibility of specific components
  const isScene1 = props.hdriBlend < 1
  const isScene2 = props.hdriBlend >= 1
  const scene3Blend = props.scene3Blend || 0
  
  // Scene 1 (hdriBlend < 1): Show Stage 1, Stage 2, Fairings only (exploded view)
  // Scene 2 (hdriBlend >= 1, scene3Blend < 0.5): Show all parts (assembled)
  // Scene 3 (scene3Blend >= 0.5): Show only Prospero with Earth environment
  const showFirstStage = scene3Blend < 0.5 // Hide in Scene 3
  const showSecondStage = scene3Blend < 0.5 // Hide in Scene 3
  const showFairings = scene3Blend < 0.5 // Hide in Scene 3
  const showEngineBrace = isScene2 && scene3Blend < 0.5 // Only in Scene 2
  const showThirdStage = isScene2 && scene3Blend < 0.5 // Only in Scene 2
  const showProspero = isScene2 // Only in Scene 2 and Scene 3

  // Calculate and log model bounds for measurements
  useEffect(() => {
    if (!nodes) return

    const modelMap = {
      'ST1_Body': nodes.ST1_Body,
      'ST2_Body': nodes.ST2_Body,
      'ThirdStage': nodes.ThirdStage,
      'Prospero': nodes.Prospero
    }

    const boundsData = {}
    Object.entries(modelMap).forEach(([name, node]) => {
      if (node && node.geometry) {
        // Calculate bounding box from geometry directly
        if (!node.geometry.boundingBox) {
          node.geometry.computeBoundingBox()
        }
        const boundingBox = node.geometry.boundingBox
        const size = boundingBox.getSize(new THREE.Vector3())
        
        boundsData[name] = {
          x: parseFloat(size.x.toFixed(4)),
          y: parseFloat(size.y.toFixed(4)),
          z: parseFloat(size.z.toFixed(4)),
          maxDimension: parseFloat(Math.max(size.x, size.y, size.z).toFixed(4))
        }
      }
    })

    setBounds(boundsData)
  }, [nodes])

  return (
    <group {...props} dispose={null} receiveShadow castShadow>
      <group position={[0, 5, 0]}>
        <group position={[0, -5, 0]}>
          <group name="Scene" receiveShadow castShadow>
            {showFairings && (
              <group name="CTRL_Fairing" position={[0, 9.421 + Stage_1_Offset, 0]} rotation={[Math.PI / 2, 0, 0]} scale={1.041} receiveShadow castShadow>
                <group
                  name="CTRL_Left_Fairing"
                  receiveShadow castShadow
                  position={[0, 0.654, -0.016]}
                  rotation={[-Math.PI / 2 + leftFairingRotation[0], 0, 0]}
                  scale={0.961}>
                  <instances.Left name="Left" receiveShadow castShadow position={[0, -9.438, -0.681]}>
                    <instances.Tip name="Tip" receiveShadow castShadow position={[0, 11.148, -0.006]} />
                  </instances.Left>
                </group>
                <group
                  name="CTRL_Right_Fairing"
                  receiveShadow castShadow
                  position={[0, -0.653, -0.016]}
                  rotation={[-Math.PI / 2 + rightFairingRotation[0], 0, 0]}
                  scale={0.961}>
                  <instances.Right
                    name="Right"
                    receiveShadow castShadow
                    position={[0, -9.438, 0.68]}
                    rotation={[Math.PI, 0, Math.PI]}
                  />
                </group>
              </group>
            )}
            <group name="CTRL_ST2" receiveShadow castShadow position={[0, 6.726 + Stage_1_Offset, 0]} rotation={[Math.PI / 2, 0, 0]} scale={1.462}>
              {showEngineBrace && (
                <instances.EngineBrace
                  name="Engine_Brace"
                  receiveShadow castShadow
                  position={[0.001, 0.004, -0.023]}
                  rotation={[-Math.PI / 2, 0, 0]}
                  scale={0.684}
                />
              )}
              <group
                name="POI_BA_ST2"
                position={[0, -1.161, -1.0]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={1.697}
              />
              <group
                name="POS_BA_ST2"
                position={[10, 0, -1.838]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={1.697}
              />
              {showSecondStage && (
                <>
                  <instances.STB
                    name="ST1B"
                    position={[0, 0, 4.6]}
                    rotation={[-Math.PI / 2, 0, 0]}
                    scale={0.684}
                  />
                  <instances.STBody
                    name="ST2_Body"
                    receiveShadow castShadow
                    position={[0, 0, -1.043]}
                    rotation={[-Math.PI / 2, 0, 0]}
                    scale={0.684}
                  />
                </>
              )}
              <group
                name="TAR_BA_ST2"
                position={[0, 0, -1.838]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={1.697}
              />
            </group>
            <group name="CTRL_ST1" receiveShadow castShadow rotation={[Math.PI / 2, 0, 0]} scale={2.083}>
              <group
                name="POI_BA_ST1"
                position={[0, -0.815, -1.42]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={1.191}
              />
              <group
                name="POS_BA_ST1"
                position={[10, 0, -1.68]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={1.191}
              />

              {showFirstStage && (
                <>
                  <instances.STBody1 name="ST1_Body" receiveShadow castShadow rotation={[-Math.PI / 2, 0, 0]} scale={0.48} />
                  <instances.STEngine
                    name="ST1_Engine"
                    receiveShadow castShadow
                    position={[0.335, 0.238, -0.186]}
                    rotation={[-Math.PI / 2, 0, 0]}
                    scale={0.48}
                  />
                </>
              )}
              <group
                name="TAR_BA_ST1"
                position={[0, 0, -1.68]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={1.191}
              />
            </group>
            <group name="CTRL_ST3" receiveShadow castShadow position={[0, 10.857 + Stage_1_Offset + Stage_2_Offset, 0]} scale={1.75}>
              {showThirdStage && (
                <instances.ThirdStage name="ThirdStage" receiveShadow castShadow position={[0, 0.055, 0]} scale={0.571} />
              )}
              <group name="POI_BA_ST3" position={[0, -0.235, -0.97]} scale={1.418} />
              <group name="POS_BA_ST3" position={[4.571, -0.235, 0]} scale={1.418} />
              <group name="TAR_BA_ST3" position={[0, -0.235, 0]} scale={1.418} />
              <group name="CTRL_ST4" receiveShadow castShadow position={[0, 0.251 + Stage_3_Offset, 0]} scale={0.805}>
                <group name="POI_BA_PRO" position={[0, -0.114, -1.205]} scale={1.761} />
                <group name="POS_BA_PRO" position={[5.677, 0.7, 0]} scale={1.761} />

                {showProspero && (
                  <instances.Prospero name="Prospero" receiveShadow castShadow position={[0, -0.243, 0]} scale={0.71} />
                )}
                <group name="TAR_BA_PRO" position={[0, 0.7, 0]} scale={1.761} />
              </group>
            </group>
          </group>
        </group>
      </group>
    </group>
  )
}
useGLTF.preload('/models/Black_Arrow.glb')
